import React, { useEffect, useRef } from "react"
import { Button } from "../../components/ui/button"
import { Check, Star, Zap, Infinity, ChevronDown } from "lucide-react"
import { useTheme } from "./theme-provider"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion"

export default function PricingSection() {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const pricingRef = useRef(null);
  const [bnplProvider, setBnplProvider] = React.useState(null);
  const [pricingSymbol, setPricingSymbol] = React.useState('$');
  const [proPrice, setProPrice] = React.useState('29');
  const [lifetimePrice, setLifetimePrice] = React.useState('947');

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const region = new URLSearchParams(window.location.search).get('region');
        if (region) {
          const r = region.toLowerCase();
          if (['ae', 'uae', 'sa', 'saudi', 'saudi arabia'].includes(r)) {
            setBnplProvider('Tabby');
          }
        }
        
        const response = await fetch(region ? `/api/pricing?region=${region}` : '/api/pricing');
        if (response.ok) {
          const data = await response.json();
          setPricingSymbol(data.symbol);
          setProPrice(data.plans.pro.monthly);
          setLifetimePrice(data.plans.lifetime.price);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchPricing();
  }, []);

  // Use IntersectionObserver to optimize rendering
  useEffect(() => {
    if (!pricingRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Add a class to trigger rendering optimizations when visible
            entry.target.classList.add('pricing-visible');
            // Prerender all cards at once when section becomes visible
            const cards = entry.target.querySelectorAll('.pricing-card');
            cards.forEach(card => {
              // Force layout recalculation only once when visible
              card.style.opacity = '1';
              card.style.transform = 'translateZ(0)';
            });
            // Disconnect once visible
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 } // Trigger when 10% of the element is visible
    );

    observer.observe(pricingRef.current);

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const plans = [
    // {
    //   name: "Starter",
    //   price: "$0",
    //   description:
    //     "Perfect for personal websites and small businesses just getting started.",
    //   features: [
    //     "1 chatbot",
    //     "500 customer queries monthly",
    //     "1000 bot responses",
    //     "Basic customization",
    //     "File based knowledge base integration",
    //     "Standard AI model",
    //     "Email support"
    //   ],
    //   popular: false,
    //   buttonText: "Get Started",
    //   buttonVariant: "outline",
    //   color: "from-gray-500/20 to-gray-500/30",
    //   darkColor: "from-gray-800/30 via-gray-700/20 to-gray-600/30",
    //   cta: "Get Early Access",
    //   iconColor: "text-gray-400",
    //   icon: <Zap size={20} className="text-gray-400" />
    // }
    {
      name: "Pro+",
      price: proPrice,
      description:
        "Ideal for growing teams that need more power and flexibility.",
      features: [
        "Unlimited chatbots",
        "10,000 user queries monthly",
        "Advanced customization suite",
        "File based knowledge base integration",
        "URL based knowledge base integration",
        "Premium AI model",
        "Analytics dashboard",
        "External integration with WooCommerce & many more",
      ],
      popular: true,
      buttonText: "Get Started",
      buttonVariant: "default",
      color: "from-purple-500/20 to-purple-500/30",
      darkColor: "from-purple-600/40 to-purple-500/20",
      cta: "Get Early Access",
      iconColor: "text-purple-400",
      icon: <Star size={20} className="text-purple-400" />
    },
    {
      name: "Lifetime",
      price: lifetimePrice,
      description: "For new founders and startups",
      features: [
        "Unlimited chatbots",
        "2500 user queries monthly",
        "Basic customization suite",
        "Premium AI model",
        "Priority support",
        "Basic analytics",
        "Full knowledge base integration",
        "Custom training"
      ],
      popular: false,
      buttonText: "Get Started",
      buttonVariant: "outline",
      color: "from-emerald-500/20 to-green-500/30",
      darkColor: "from-emerald-600/30 via-green-600/20 to-teal-600/30",
      special: true,
      cta: "Get Early Access",
      iconColor: "text-emerald-400",
      icon: <Infinity size={20} className="text-emerald-400" />
    },
    {
      name: "Enterprise",
      price: "Custom",
      description:
        "For custom requirements and enterprise level support.",
      features: [
        "Unlimited chatbots as Add-ons",
        "Custom user queries monthly",
        "Integration & brand alignment support",
        "Custom AI model",
        "Live database integration",
        "On-Demand analytics dashboard",
        "Priority support",
        "White-labeling"
      ],
      popular: false,
      buttonText: "Enquire",
      buttonVariant: "outline",
      color: "from-gray-500/20 to-gray-500/30",
      darkColor: "from-gray-800/30 via-gray-700/20 to-gray-600/30",
      cta: "Get Early Access",
      iconColor: "text-gray-400",
      icon: <Zap size={20} className="text-gray-400" />
    },
  ];

  // Detailed feature comparison for all plans
  const allFeatures = [
    {
      category: "Chatbot Creation",
      features: [
        { name: "Number of chatbots", proPlus: "Unlimited", lifetime: "Unlimited", enterprise: "Unlimited as Add-ons" },
        { name: "User queries monthly", proPlus: "10,000", lifetime: "2,500", enterprise: "Custom" },
        { name: "Leads capture monthly", proPlus: "1000", lifetime: "250", enterprise: "Unlimited" }
      ]
    },
    {
      category: "Customization",
      features: [
        { name: "Customization options", proPlus: "Advanced suite", lifetime: "Basic suite", enterprise: "Custom" },
        { name: "White-labeling", proPlus: "✗", lifetime: "✗", enterprise: "✓" },
        { name: "Bring your own LLM", proPlus: "✗", lifetime: "✗", enterprise: "✓" }
      ]
    },
    {
      category: "Knowledge Base",
      features: [
        { name: "File uploads (PDF, DOCX, TXT)", proPlus: "✓", lifetime: "✓", enterprise: "✓" },
        { name: "URL scraping", proPlus: "✓", lifetime: "✓", enterprise: "✓" },
        { name: "Custom training", proPlus: "✗", lifetime: "✓", enterprise: "✓" },
        { name: "Knowledge base integration", proPlus: "File & URL based", lifetime: "Full integration", enterprise: "Everything +  Live database integration" }
      ]
    },
    {
      category: "AI Capabilities",
      features: [
        { name: "AI model", proPlus: "Premium", lifetime: "Premium", enterprise: "Custom" },
        { name: "Context memory", proPlus: "Extended", lifetime: "Extended", enterprise: "Extended" },
        { name: "Conversation history", proPlus: "Unlimited", lifetime: "3 Months", enterprise: "Unlimited" }
      ]
    },
    {
      category: "Integration",
      features: [
        { name: "WooCommerce integration", proPlus: "✓", lifetime: "✗", enterprise: "✓" },
        { name: "External integrations", proPlus: "Many more", lifetime: "✗", enterprise: "Custom" },
        { name: "Integration Assistance", proPlus: "✓", lifetime: "✓", enterprise: "✓" }
      ]
    },
    {
      category: "Analytics & Support",
      features: [
        { name: "Analytics dashboard", proPlus: "Advanced", lifetime: "Basic", enterprise: "Custom" },
        { name: "User feedback collection", proPlus: "✓", lifetime: "✓", enterprise: "✓" },
        { name: "Integration Support", proPlus: "✓", lifetime: "✓", enterprise: "Priority" },
        { name: "Post Integration Support", proPlus: "✓", lifetime: "✓", enterprise: "Priority" }
      ]
    }
  ];

  // Simplified dark mode background effects without excessive blur and overlays
  const renderDarkModeEffects = (index) => {
    if (!isDarkMode) return null;

    return (
      <div className="absolute top-0 right-0 w-full h-full overflow-hidden rounded-xl -z-5 will-change-transform">
        <div
          className={`absolute -top-24 -right-24 w-48 h-48 rounded-full opacity-20 ${index === 0 ? 'bg-gray-500' : index === 1 ? 'bg-purple-500' : 'bg-emerald-500'
            }`}
          style={{
            filter: 'blur(24px)',
            willChange: 'transform, opacity'
          }}
        ></div>
      </div>
    );
  };

  return (
    <section
      id="pricing"
      ref={pricingRef}
      className={`pt-8 pb-20 relative ${isDarkMode ? 'bg-gradient-to-b from-gray-900 to-gray-950' : 'bg-background'
        }`}
    >
      {/* Simplified dark mode background effects */}
      {isDarkMode && (
        <>
          <div
            className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-br from-purple-900/10 via-transparent to-blue-900/10"
            style={{
              filter: 'blur(24px)',
              willChange: 'transform'
            }}
          ></div>
        </>
      )}

      <div className="container px-4 md:px-6 relative z-10">
        <div className="flex flex-col items-center justify-center space-y-4 text-center">
          <div className="space-y-2">
            <div className={`inline-block rounded-lg ${isDarkMode
              ? 'bg-purple-500/20 text-purple-300'
              : 'bg-primary/10 text-primary'
              } px-3 py-1 text-sm`}>
              Pricing
            </div>
            <h2 className={`text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl ${isDarkMode ? 'text-white' : ''
              }`}>
              Simple, transparent pricing
            </h2>
            <p className={`max-w-[900px] ${isDarkMode ? 'text-gray-300' : 'text-muted-foreground'
              } md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed`}>
              Choose the perfect plan for your needs with transparent pricing and no hidden fees.
            </p>
          </div>
        </div>
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8 pt-12">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`pricing-card flex flex-col rounded-xl ${isDarkMode
                ? `border border-gray-800/50 bg-gradient-to-br ${plan.darkColor} shadow-lg`
                : 'border bg-card'
                } p-6 relative overflow-hidden h-full transition-all duration-300 hover:shadow-md ${plan.popular
                  ? isDarkMode
                    ? "border-purple-500/70 ring-1 ring-purple-500/70"
                    : "border-primary ring-1 ring-primary"
                  : index === 1 // Lifetime plan gets special outline
                    ? isDarkMode
                      ? "border-emerald-500/70 ring-1 ring-emerald-500/70"
                      : "border-emerald-500 ring-1 ring-emerald-500"
                    : isDarkMode
                      ? index === 0
                        ? "border-purple-700/30 hover:border-purple-600/40"
                        : "border-gray-700/30 hover:border-gray-600/40"
                      : ""
                }`}
              style={{
                opacity: 0,
                transform: 'translateZ(0)',
                transition: 'opacity 0.3s ease-in-out',
                willChange: 'opacity, transform'
              }}
            >
              {/* Simplified background effects */}
              {renderDarkModeEffects(index)}

              {/* Main content container with flex layout */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Header content - badges, title, price */}
                <div className="mb-6">
                  {/* Special tag for lifetime plan */}
                  {index === 1 && (
                    <div className={`inline-block rounded-full ${isDarkMode
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                      : 'bg-gradient-to-r from-emerald-500 to-green-500 text-white'
                      } px-3 py-1 text-xs self-start mb-4`}>
                      Limited Pre-Launch Offer
                    </div>
                  )}
                  {index === 2 && (
                    <div className={`inline-block rounded-full ${isDarkMode
                      ? ''
                      : ''
                      } px-3 py-1 text-xs self-start mb-4`}>

                    </div>
                  )}

                  {plan.popular && (
                    <div className={`inline-block rounded-full ${isDarkMode
                      ? 'bg-purple-600 text-white'
                      : 'bg-primary text-primary-foreground'
                      } px-3 py-1 text-xs self-start mb-4`}>
                      Most Popular
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {plan.icon && (
                        <div className={`rounded-full ${isDarkMode
                          ? index === 0
                            ? 'bg-gray-500/20 p-2'
                            : index === 1
                              ? 'bg-purple-500/20 p-2'
                              : 'bg-emerald-500/20 p-2'
                          : ''
                          }`}>
                          {plan.icon}
                        </div>
                      )}
                      <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : ''}`}>{plan.name}</h3>
                    </div>
                    <div className="flex items-baseline">

                      <span 
                        className={`text-3xl font-bold ${isDarkMode ? 'text-white' : ''} flex items-center`}
                      >
                        <span style={{ fontFamily: "Dirham, sans-serif", fontSize: '0.85em', fontWeight: 500, lineHeight: 1, marginRight: '6px' }}>
                          {pricingSymbol}
                        </span>
                        {plan.price}
                      </span>
                      {plan.name === "Enterprise" && (
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-muted-foreground'} ml-1`}>/month</span>
                      )}
                      {plan.name === "Lifetime" && (
                        <div className="flex flex-col ml-2">
                          <span className={`${isDarkMode ? 'text-gray-400' : 'text-muted-foreground'} ml-1`}>One Time</span>
                          {bnplProvider === 'Tabby' && (
                            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                              or 4 payments with <img src="https://mintcdn.com/tabby-5f40add6/1f-EIKwKexPNgWGm/logo/tabby-badge.svg" alt="Tabby" className="h-4 object-contain ml-1" />
                            </div>
                          )}
                        </div>
                      )}
                      {plan.name === "Pro+" && (
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-muted-foreground'} ml-1`}>/month</span>
                      )}

                    </div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-muted-foreground'}`}>{plan.description}</p>
                  </div>
                </div>

                {/* Features - growing section */}
                <div className="grow">
                  <ul className="space-y-2">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <Check className={`h-4 w-4 ${isDarkMode
                          ? plan.iconColor
                          : 'text-primary'
                          } mr-2`} />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : ''}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bottom section with fixed alignment */}
                <div className={`mt-6 pt-6 ${isDarkMode ? 'border-t border-gray-800/50' : 'border-t'}`}>
                  <Button
                    className={`w-full ${isDarkMode && plan.buttonVariant === "outline"
                      ? index === 0
                        ? "bg-gradient-to-r from-gray-700/50 to-gray-800/50 border-gray-700/50 text-white hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-800 hover:border-gray-600 hover:text-white"
                        : "bg-gradient-to-r from-emerald-700/50 to-teal-700/50 border-emerald-700/50 text-white hover:bg-gradient-to-r hover:from-emerald-700 hover:to-teal-700 hover:border-emerald-600 hover:text-white"
                      : ""
                      } ${isDarkMode && plan.buttonVariant === "default"
                        ? "bg-purple-600 hover:bg-purple-700"
                        : ""
                      }`}
                    variant={plan.buttonVariant}
                    size="lg"
                    onClick={() => {
                      // Store only the essential plan data in localStorage and redirect to get-started
                      const planData = {
                        id: plan.id,
                        name: plan.name,
                        price: plan.price,
                        priceValue: plan.priceValue,
                        description: plan.description,
                        features: plan.features,
                        popular: plan.popular,
                        planType: plan.planType
                      };
                      localStorage.setItem('selectedPlan', JSON.stringify(planData));
                      window.location.href = '/get-started';
                    }}
                  >
                    {plan.buttonText}
                  </Button>

                  {/* Special note for lifetime plan */}
                  {index === 0 && (
                    <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-300' : 'text-black-300'}`}>
                      Perfect for analyzing your use case
                    </p>
                  )}
                  {index === 1 && (
                    <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                      One-time payment, lifetime access
                    </p>
                  )}
                  {index === 2 && (
                    <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-300' : 'text-black-300'}`}>
                      Designed for growing teams
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Plan Comparison Accordion */}
        <div className="mt-16 max-w-5xl mx-auto">
          <Accordion type="single" collapsible className={`${isDarkMode
            ? 'border border-gray-800/50 bg-gradient-to-br from-gray-800/30 via-gray-700/20 to-gray-600/30 backdrop-blur-sm shadow-lg rounded-xl overflow-hidden'
            : 'border bg-card rounded-xl shadow-sm'
            }`}>
            <AccordionItem value="comparison" className="border-0 last:border-0">
              <AccordionTrigger className={`px-6 ${isDarkMode ? 'text-white' : ''}`}>
                <div className="flex items-center gap-2">
                  {isDarkMode ? (
                    <div className="rounded-full bg-purple-500/20 p-2">
                      <ChevronDown className="h-4 w-4 text-purple-400" />
                    </div>
                  ) : (
                    <div className="rounded-full bg-primary/10 p-2">
                      <ChevronDown className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <span className="font-medium">Detailed Plan Comparison</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6">
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead>
                      <tr>
                        <th className={`py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Feature
                        </th>
                        <th className={`py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Pro+
                        </th>
                        <th className={`py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Lifetime
                        </th>
                        <th className={`py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          Enterprise
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {allFeatures.map((categoryGroup, categoryIndex) => (
                        <React.Fragment key={categoryIndex}>
                          <tr className={categoryIndex !== 0 ? `${isDarkMode ? 'border-t-4 border-gray-800' : 'border-t-4 border-gray-100'}` : ''}>
                            <td
                              colSpan="4"
                              className={`py-2 font-semibold ${isDarkMode ? 'text-gray-200 bg-gray-800/30' : 'text-gray-700 bg-gray-50/80'} px-4`}
                            >
                              {categoryGroup.category}
                            </td>
                          </tr>
                          {categoryGroup.features.map((feature, featureIndex) => (
                            <tr
                              key={`${categoryIndex}-${featureIndex}`}
                              className={`${isDarkMode ? 'even:bg-gray-800/20' : 'even:bg-gray-50/50'}`}
                            >
                              <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                {feature.name}
                              </td>
                              <td className={`py-3 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {feature.proPlus}
                              </td>
                              <td className={`py-3 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {feature.lifetime}
                              </td>
                              <td className={`py-3 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {feature.enterprise}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  )
}
